/**
 * Diff two consecutive RoomViews into the discrete things that just happened
 * (opponent moved, my turn started, game ended, presence changed…). The view
 * sync (game/sync.ts) turns these into toasts, sounds, announcements and
 * celebrations. Pure and unit-tested.
 */
import type { GameResult, MoveRecord, RoomView, Seat } from '@wordloom/engine';
import { sentence } from './text';

export type ViewEvent =
  | { type: 'opponent-joined'; name: string }
  | { type: 'game-started'; firstSeat: Seat; gameNumber: number }
  | { type: 'move'; record: MoveRecord; mine: boolean }
  | { type: 'my-turn' }
  | { type: 'game-ended'; result: GameResult }
  | { type: 'opponent-disconnected'; name: string }
  | { type: 'opponent-reconnected'; name: string }
  | { type: 'rematch-requested'; name: string }
  /** My own rematch vote was recorded (want) or withdrawn — the requester hears it too. */
  | { type: 'my-rematch-vote'; want: boolean };

export function opponentSeat(view: Pick<RoomView, 'you'>): Seat {
  return view.you === 0 ? 1 : 0;
}

export function seatName(view: Pick<RoomView, 'seats'>, seat: Seat): string {
  return view.seats[seat]?.name ?? (seat === 0 ? 'Player 1' : 'Player 2');
}

/** A game just began: who starts, and "your turn" when that is me. */
function startEvents(next: RoomView): ViewEvent[] {
  const game = next.game;
  if (!game) return [];
  const events: ViewEvent[] = [{ type: 'game-started', firstSeat: game.firstSeat, gameNumber: next.gameNumber }];
  if (game.status === 'playing' && game.currentSeat === next.you) events.push({ type: 'my-turn' });
  return events;
}

export function diffViews(prev: RoomView | null, next: RoomView): ViewEvent[] {
  if (!prev) {
    // First view of a room (page load / join): nothing "happened", we just
    // arrived — except that a game with no moves yet has only just started.
    // The joiner's first view already holds the running game, and without
    // this they would get no "Game on" at all (the creator gets it from the
    // waiting → playing diff).
    const game = next.game;
    return game?.status === 'playing' && game.history.length === 0 ? startEvents(next) : [];
  }
  // A different room (late push after switching): not a change of this one.
  if (prev.code !== next.code) return [];
  const events: ViewEvent[] = [];
  const opp = opponentSeat(next);
  const oppName = seatName(next, opp);

  if (!prev.seats[opp] && next.seats[opp]) events.push({ type: 'opponent-joined', name: oppName });

  const newGame = next.game && (prev.gameNumber !== next.gameNumber || !prev.game);
  if (newGame && next.game) {
    events.push(...startEvents(next));
  } else if (prev.game && next.game) {
    const before = prev.game.history.length;
    for (const record of next.game.history.slice(before)) {
      events.push({ type: 'move', record, mine: record.seat === next.you });
    }
    if (prev.game.status === 'playing' && next.game.status === 'finished' && next.game.result) {
      events.push({ type: 'game-ended', result: next.game.result });
    } else if (
      next.game.status === 'playing' &&
      next.game.currentSeat === next.you &&
      (prev.game.currentSeat !== next.you || prev.game.turnNumber !== next.game.turnNumber)
    ) {
      events.push({ type: 'my-turn' });
    }
  }

  const wasConnected = prev.seats[opp]?.connected;
  const isConnected = next.seats[opp]?.connected;
  if (prev.seats[opp] && next.seats[opp]) {
    if (wasConnected && !isConnected) events.push({ type: 'opponent-disconnected', name: oppName });
    if (!wasConnected && isConnected) events.push({ type: 'opponent-reconnected', name: oppName });
  }

  if (!prev.rematchVotes.includes(opp) && next.rematchVotes.includes(opp) && next.status === 'finished') {
    events.push({ type: 'rematch-requested', name: oppName });
  }
  // My vote, while still on the game-over screen (a vote that starts the
  // rematch arrives as a new game instead, and is announced as that).
  const voted = next.rematchVotes.includes(next.you);
  if (next.status === 'finished' && prev.rematchVotes.includes(next.you) !== voted) {
    events.push({ type: 'my-rematch-vote', want: voted });
  }
  return events;
}

/** "You won, 312 to 290." / "Alex won, 312 to 290." / "Draw, 250 all." */
export function gameEndText(result: GameResult, view: Pick<RoomView, 'you' | 'seats'>): string {
  const scores = result.finalScores;
  if (result.winner === null) return `Draw, ${scores[0]} all.`;
  const loser: Seat = result.winner === 0 ? 1 : 0;
  const who = result.winner === view.you ? 'You' : seatName(view, result.winner);
  return `${who} won, ${scores[result.winner]} to ${scores[loser]}.`;
}

/** describeMove as a spoken sentence: one terminal mark, never "bingo!.". */
export function moveLabel(record: MoveRecord, name: string): string {
  return sentence(describeMove(record, name));
}

/** "Alex played QUIRK for 38" / "Alex exchanged 3 tiles" / "Alex passed". */
export function describeMove(record: MoveRecord, name: string): string {
  switch (record.type) {
    case 'play': {
      const main = record.words[0]?.word ?? 'a word';
      const extra = record.words.length > 1 ? ` (+${record.words.length - 1} more)` : '';
      return `${name} played ${main}${extra} for ${record.score}${record.bingo ? ' — bingo!' : ''}`;
    }
    case 'exchange':
      return `${name} exchanged ${record.exchanged} ${record.exchanged === 1 ? 'tile' : 'tiles'}`;
    case 'pass':
      return `${name} passed`;
    case 'timeout':
      return `${name} ran out of time`;
    case 'resign':
      return `${name} resigned`;
    case 'forfeit':
      return `${name} forfeited`;
  }
}
